import React, { useContext, useRef } from 'react';
import useOutsideClick from '../../hooks/useOutsideClick';
import useWindowSize from '../../hooks/useWindowSize';
import FilterButton from '../FilterButton';
import { ListingMapContext } from '../../context/ListingMapContext';
import PaginationResults from './PaginationResults';
import FilterToggle from './FilterToggle';
import WindowQuery from '../../utils/WindowQuery';
import TypeDropdown from './Dropdowns/TypeDropdown';
import DropdownContainer from '../DropdownContainer';
import SortDropdown from './Dropdowns/SortDropdown';
import CategoryDropdown from './Dropdowns/CategoryDropdown';

function ListingFilterBar({ type }) {
  const updateQuery = new WindowQuery();

  const {
    mapCategories,
    sortByVisible,
    categoriesVisible,
    typesVisible,
    listingSortBy,
    setDropdown,
    showFavorites,
    isTypeIn,
    meta,
    clearAllFilters,
    showDeals,
    setShowDeals,
  } = useContext(ListingMapContext);

  const { width: windowWidth } = useWindowSize();

  const typesRef = useRef(null);
  const categoriesRef = useRef(null);
  const sortRef = useRef(null);

  const isMobile = !(windowWidth > 768);

  let filterDropdownText = 'Category';
  if (isMobile) {
    if (mapCategories.length && showFavorites) {
      // filters for categories and favorites
      filterDropdownText = `Filters • ${mapCategories.length + 1}`;
    } else if (mapCategories.length && !showFavorites) {
      // filters for only categories
      filterDropdownText = `Filters • ${mapCategories.length}`;
    } else {
      // no filters
      filterDropdownText = 'Filters';
    }
  } else if (mapCategories.length) {
    filterDropdownText = `Categories • ${mapCategories.length}`;
  }

  const { per_page, current_page, total_count } = meta || {};

  return (
    <div className="filter-bar">
      <section className="filters-container">
        <div className="filter-button-group" ref={typesRef}>
          <FilterButton
            className={`${isTypeIn.length ? 'btn--dark' : 'btn--light'}`}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setDropdown('types');
            }}
          >
            {isTypeIn.length
              ? `${isTypeIn.length} Type${isTypeIn.length > 1 ? 's' : ''}`
              : 'Types'}
          </FilterButton>
          {!isMobile && (
            <DropdownContainer visible={typesVisible}>
              <TypeDropdown />
            </DropdownContainer>
          )}
        </div>
        <div className="filter-button-group" ref={categoriesRef}>
          <FilterButton
            className={`${mapCategories.length ? 'btn--dark' : 'btn--light'}`}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setDropdown('categories');
            }}
          >
            {filterDropdownText}
          </FilterButton>
          {!isMobile && (
            <DropdownContainer visible={categoriesVisible}>
              <CategoryDropdown mapType={type} />
            </DropdownContainer>
          )}
        </div>
        <div className="filter-button-group" ref={sortRef}>
          <FilterButton
            className={`${listingSortBy ? 'btn--dark' : 'btn--light'}`}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              setDropdown('sort_by');
            }}
          >
            {listingSortBy ? `By ${listingSortBy.label}` : 'Sort By'}
          </FilterButton>
          {!isMobile && (
            <DropdownContainer visible={sortByVisible}>
              <SortDropdown mapType={type} />
            </DropdownContainer>
          )}
        </div>
        {!isMobile && (
          <button
            type="button"
            name="Clear All Filters"
            title="Clear All Filters"
            aria-label="Clear All Filters"
            className="btn btn--small btn--link"
            onClick={() => {
              clearAllFilters(type);
            }}
          >
            Clear All
          </button>
        )}
        {total_count > 0 && !isMobile && (
          <PaginationResults
            firstIndex={per_page * current_page - per_page + 1}
            lastIndex={Math.min(per_page * current_page, total_count)}
            totalCount={total_count}
            queryType="listings"
          />
        )}
      </section>

      <FilterToggle
        toggleLabel={showDeals ? 'Hide Deals' : 'Show Only Deals'}
        toggleView={() => {
          if (showDeals) {
            updateQuery.remove('show_deals');
          } else {
            updateQuery.add('show_deals', true);
          }
          setShowDeals(!showDeals);
        }}
        checked={showDeals}
      />
    </div>
  );
}

export default ListingFilterBar;
